/**
 * Module F-Lite — typed engine API client with session-token auth.
 *
 * Reads VITE_ENGINE_URL at build time (the engine origin, e.g.
 *   https://api.siteskick.com). The session token is loaded from
 * localStorage at module-init AND on every request (so logout/login in
 * another tab is honored on the next call). If no token is present,
 * requests fire without Authorization — useful for /api/auth/login (which
 * is public anyway).
 *
 * On 401 from the engine, the client clears the stored token and emits a
 * 'sitekit:session-expired' window event so the UI can redirect to /login.
 */

const ENGINE_URL = (import.meta.env.VITE_ENGINE_URL ?? '').replace(/\/+$/, '');

if (!ENGINE_URL) {
  // eslint-disable-next-line no-console
  console.warn('VITE_ENGINE_URL is empty — API calls will fail. Set it in .env.local.');
}

const TOKEN_KEY = 'sitekit:session-token';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch { /* private-mode browser w/o storage; ignore */ }
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `Engine API ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${ENGINE_URL}${path.startsWith('/') ? path : '/' + path}`;
  const token = getToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // Session expired (or never had one). Clear + notify the app.
    setToken(null);
    window.dispatchEvent(new Event('sitekit:session-expired'));
  }
  if (!res.ok) {
    const text = await res.text();
    let body: unknown = text;
    try { body = JSON.parse(text); } catch { /* keep raw text */ }
    const message =
      typeof body === 'object' && body && 'errorMessage' in body
        ? String((body as { errorMessage: string }).errorMessage)
        : typeof body === 'object' && body && 'error' in body
          ? String((body as { error: string }).error)
          : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  // --- auth ---
  login: (email: string, password: string) =>
    request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () =>
    request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  me: () => request<MeResponse>('/api/auth/me'),

  // --- projects (RLS-filtered server-side) ---
  listProjects: () => request<{ projects: ProjectSummary[] }>('/api/projects'),
  getProjectState: (projectId: string) =>
    request<ProjectState>(`/api/projects/${projectId}/state`),
  createProject: (body: CreateProjectBody) =>
    request<CreateProjectResponse>('/api/projects', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approveSkeleton: (projectId: string) =>
    request<{ ok: true; finalSkeletonVersion: number }>(
      `/api/projects/${projectId}/skeleton/approve`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  startBuildRun: (projectId: string) =>
    request<{ buildRunId: string; jobId: string }>(
      `/api/projects/${projectId}/buildruns`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  acceptProject: (projectId: string) =>
    request<{ ok: true; acceptedAt?: string; alreadyAccepted?: boolean }>(
      `/api/projects/${projectId}/accept`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  // --- dashboard logo replace (no buildrun, no lock-guard) ---
  // Lets the operator swap a project's logo at any time. The new logo only
  // affects FUTURE builds; in-flight builds keep using whatever logoUrl was
  // on the brand at phase-start.
  replaceLogo: (
    projectId: string,
    fileBase64: string,
    format: string,
    opts: { autoRebuild?: boolean } = {},
  ) =>
    request<ReplaceLogoResponse>(`/api/projects/${projectId}/logo`, {
      method: 'POST',
      body: JSON.stringify({
        fileBase64,
        format,
        ...(typeof opts.autoRebuild === 'boolean' ? { autoRebuild: opts.autoRebuild } : {}),
      }),
    }),
  // --- deploy ---
  deploy: (projectId: string, snapshotId: string, domainConfigId: string) =>
    request<{ jobId: string }>(`/api/projects/${projectId}/deployments`, {
      method: 'POST',
      body: JSON.stringify({ snapshotId, domainConfigId, policy: 'pinned_snapshot' }),
    }),
  // --- promote to production ---
  promoteToProduction: (
    projectId: string,
    hostname: string,
    aliases: string[] = [],
  ) =>
    request<PromoteResponse>(
      `/api/admin/projects/${projectId}/promote-to-production`,
      {
        method: 'POST',
        body: JSON.stringify({ hostname, aliases }),
      },
    ),
  // --- Module G — managed domains ---
  // Strict "available to attach right now" filter — for the project-detail
  // Connect-Domain dropdown. Drops domains already in production use.
  availableDomains: () =>
    request<{ domains: ManagedDomain[] }>('/api/domains/available'),
  // Full inventory owned by the current user (every CF zone we've synced).
  // For Aviv (the single operator) this returns every Cloudflare zone he
  // owns. Used by the Domains page so in-use + available + unattached
  // zones all show with correct labels — without this query param the
  // page used to fall back to live-URL guesses for in-use sites and
  // mislabel them as "not yet synced as a CF zone".
  allDomains: () =>
    request<{ domains: ManagedDomain[] }>('/api/domains/available?includeAll=true'),
  // Re-enumerates every Cloudflare zone the engine's CF API token can see and
  // upserts the catalog. Returns counts so the UI can confirm what changed.
  syncDomains: () =>
    request<SyncDomainsResponse>('/api/admin/domains/sync', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  connectDomain: (projectId: string, managedDomainId: string) =>
    request<ConnectDomainResponse>(
      `/api/projects/${projectId}/connect-domain`,
      { method: 'POST', body: JSON.stringify({ managedDomainId }) },
    ),
  // --- GitOps eject ---
  // Fires the REAL (one-way) eject: assembles a standalone repo, creates it on
  // GitHub, pushes, injects CI secrets, and stamps Project.ejectedAt. Async (202);
  // the repo URL arrives via /state (project.ejectedRepoUrl) once the worker runs.
  ejectProject: (projectId: string) =>
    request<EjectResponse>(`/api/projects/${projectId}/eject`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  // --- indexing (Google Instant Indexing + IndexNow) ---
  // Engine wiring is under /api/admin/* (commit 48c60bc). The RLS middleware
  // only guards /api/projects/:id/* paths, so session-authed users can call
  // these admin paths directly — no 401, no logout. In our single-tenant
  // setup (Aviv owns every project) this is safe.
  indexPing: (
    projectId: string,
    mode: 'auto' | 'all' | 'stale' | 'homepage' = 'auto',
  ) =>
    request<{ jobId: string; mode: string }>(
      `/api/admin/projects/${projectId}/index-ping?mode=${mode}`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  indexEvents: (projectId: string, limit = 20) =>
    request<{ events: IndexingEvent[] }>(
      `/api/admin/projects/${projectId}/index-ping/events?limit=${limit}`,
    ),
  // --- validation ---
  latestValidation: (projectId: string) =>
    request<{ report: ValidationReport }>(`/api/projects/${projectId}/validation/latest`),
  // --- snapshot download (binary) ---
  // Uses fetch+blob because <a href> can't carry Authorization.
  downloadSnapshot: async (projectId: string, snapshotId: string): Promise<void> => {
    const url = `${ENGINE_URL}/api/projects/${projectId}/snapshots/${snapshotId}/download`;
    const token = getToken();
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      throw new ApiError(res.status, await res.text().catch(() => null), `download failed: ${res.status}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const filenameMatch = cd.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1] ?? `snapshot-${snapshotId}.tar.gz`;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  },
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: { id: string; email: string; displayName?: string };
}

export interface MeResponse {
  user: { id: string; email: string; displayName?: string; lastLoginAt?: string } | null;
  bearerAuth?: boolean;
}

export interface ProjectSummary {
  id: string;
  brandId: string;
  kitId: string;
  liveDeploymentId?: string;
  currentBuildRunId?: string;
  ownerUserId?: string;
  acceptedAt?: string;
  autoAccept: boolean;
  displayName?: string;
  /**
   * Phase 3 — connect-domain lifecycle state. Engine writes this through
   * the FSM during a connect-domain flow; the UI polls it via
   * /api/projects/:id/state and renders the matching progress step in
   * PromoteDialog.
   *
   * undefined  — no connect-domain has ever run on this project
   * 'attaching' / 'dns_provisioning' / 'cert_pending'
   *   — CF Pages + zone setup phases (sub-second to ~30s each)
   * 'building' / 'deploying' / 'reachability_check'
   *   — BuildRun + deploy + post-deploy probe (~5 min total)
   * 'live'    — probe confirmed canonical host serves successfully
   * 'failed'  — see domainConnectError for the operator-readable reason
   */
  domainConnectState?:
    | 'attaching'
    | 'dns_provisioning'
    | 'cert_pending'
    | 'building'
    | 'deploying'
    | 'reachability_check'
    | 'indexing_verify'
    | 'audit_verify'
    | 'live'
    | 'failed';
  /** Phase 3 — operator-readable failure reason when state==='failed'. */
  domainConnectError?: string;
  /**
   * Post-deploy verify (migration 0014) — recoverable warnings observed
   * during the indexing + audit verify steps that carried forward into
   * the 'live' terminal state. Examples: "Google Indexing: 61/87 URLs
   * deferred to next quota cycle", "IndexNow: auto-recovered after
   * metadata re-render". UI renders each string as a panel item.
   */
  domainConnectWarnings?: string[];
  /** GitOps eject — set once the site is handed off to a standalone agent-owned
   *  repo. `ejectedAt` is the one-way hard-stop timestamp (only on a REAL eject);
   *  `ejectedRepoUrl` is the GitHub repo URL (set on real ejects and dry runs).
   *  The UI polls these to show the "Site Ready & Ejected" clone screen. */
  ejectedAt?: string;
  ejectedRepoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

/** Phase 3 — exported union mirroring the engine's DomainConnectState. */
export type DomainConnectState = NonNullable<ProjectSummary['domainConnectState']>;

export interface ProjectState {
  project: ProjectSummary;
  buildRun?: BuildRun;
  lockHolder: string | null;
  lockExpiresAt: string | null;
  liveUrl?: string | null;
  buildRunCount?: number;
  branding?: BrandingSummary;
}

export interface BrandingSummary {
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
}

export interface ReplaceLogoResponse {
  ok: true;
  logoUrl: string;
  logoWidth?: number;
  logoHeight?: number;
  prevLogoUrl?: string;
  /** True when the engine rolled the latest BuildRun back and enqueued a resume. */
  rebuildTriggered: boolean;
  /** The BuildRun that was re-enqueued (present iff `rebuildTriggered`). */
  buildRunId?: string;
}

export interface BuildRun {
  id: string;
  projectId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'halted_manual_edits' | 'awaiting_plan_decision' | 'paused_resumable';
  lastCompletedPhase?: string;
  snapshotId?: string;
  totalCostUsd?: number;
  startedAt: string;
  completedAt?: string;
  errorCode?: string;
  errorMessage?: string;
  renderMode?: 'full' | 'metadata_only';
}

export interface CreateProjectBody {
  brand: unknown;
  kitId: string;
  logo?: { fileBase64: string; format: string };
}

export interface CreateProjectResponse {
  projectId: string;
  brandId: string;
  kitId: string;
  domainConfigId: string;
  stagingHostname: string;
  warnings?: Array<{ code: string; message: string }>;
}

export interface PromoteResponse {
  domainConfigId: string;
  oldRole: string;
  newRole: string;
  oldCanonicalHost: string;
  newCanonicalHost: string;
  aliases: string[];
  buildRunId: string;
  jobId: string;
  estimatedDurationSec: number;
  nextSteps: string[];
}

export interface ManagedDomain {
  id: string;
  cfZoneId: string;
  rootDomain: string;
  ownerUserId?: string;
  attachedToCfPagesProject?: string;
  attachedAt?: string;
  kind: 'client' | 'agency';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SyncDomainsResponse {
  ok: true;
  zonesFound: number;
  defaultOwnerEmail: string | null;
  defaultOwnerResolved: boolean;
  upserts: Array<{
    hostname: string;
    cfZoneId: string;
    created: boolean;
    assignedToDefault: boolean;
  }>;
}

export interface ConnectDomainResponse {
  domainConfigId: string;
  rootDomain: string;
  oldRole: string;
  newRole: string;
  oldCanonicalHost: string;
  buildRunId: string;
  jobId: string;
  estimatedDurationSec: number;
  nextSteps: string[];
}

export interface EjectResponse {
  jobId: string;
  projectId: string;
  /** 'ejecting' for a real eject, 'ejecting (dry run)' for a dry run. */
  status: string;
  dryRun?: boolean;
}

export interface ValidationReport {
  id: string;
  outcome: 'pass' | 'fail';
  failureSubtype?: string;
  data: {
    pages: Record<string, { outcome: 'pass' | 'fail'; issues: ValidationIssue[] }>;
    global: Record<string, ValidationIssue[]>;
  };
}

export interface ValidationIssue {
  ruleId: string;
  severity: 'blocking' | 'warning';
  message: string;
}

/**
 * Status union for IndexingEvent rows. MUST match
 * `IndexingEventStatus` in packages/db/schema.ts. The engine writes
 * 'ok' on success, 'skipped' when quota exhausted, 'failed' otherwise.
 * (Historical drift: this field used to claim 'success', but the engine
 * has always written 'ok'. The IndexButton's status pill never rendered
 * until this contract was aligned.)
 */
export type IndexingEventStatus = 'ok' | 'skipped' | 'failed';

export interface IndexingEvent {
  id: string;
  projectId: string;
  deploymentId?: string;
  snapshotId?: string;
  provider: 'google' | 'indexnow' | string;
  url: string;
  status: IndexingEventStatus;
  responseCode?: number;
  responseBody?: string;
  errorMessage?: string;
  createdAt: string;
}
